/**
 * useNetworkStatus Hook
 *
 * Tracks browser online/offline state reactively.
 * Integrates with TanStack Query's onlineManager so queries
 * pause automatically when offline and refetch on reconnect.
 */

import { useSyncExternalStore, useCallback } from "react";
import { onlineManager } from "@tanstack/react-query";

function subscribe(callback: () => void): () => void {
  window.addEventListener("online", callback);
  window.addEventListener("offline", callback);
  return () => {
    window.removeEventListener("online", callback);
    window.removeEventListener("offline", callback);
  };
}

function getSnapshot(): boolean {
  return navigator.onLine;
}

function getServerSnapshot(): boolean {
  return true;
}

export function useNetworkStatus() {
  const isOnline = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Keep TanStack Query's onlineManager in sync
  onlineManager.setOnline(isOnline);

  const checkConnection = useCallback(async (): Promise<boolean> => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      await fetch("/tonconnect-manifest.json", {
        method: "HEAD",
        cache: "no-store",
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return true;
    } catch {
      return false;
    }
  }, []);

  return { isOnline, checkConnection };
}
